import httpClient from '@syneto/http-client';
import { jobEndpoints } from './endpoints';

const TRIAL_PRODUCT_FAILED_MESSAGE = 'Failed to create trial product.';
const TRIAL_PRODUCT_SUCCEEDED_MESSAGE = 'Trial product created successfully.';
const TRIAL_ACTIVATION_KEYS_FAILED_MESSAGE = 'Failed to poll trial activation keys...';

export class LicensingService {
	constructor(restAxios) {
		this.restAxios = restAxios;
	}

	createTrialProduct = (email, companyId) => {
		return new Promise((resolve, reject) => {
			this.restAxios.post('/licensing/trial-products', { email, companyId })
				.then((response) => {
					const { jobId } = response.data;
					const poll = httpClient.poll(
						jobEndpoints.job(jobId),
						{
							runAtOnce: true
						},
						(response) => {
							// A throw in here escapes into the polling internals rather than rejecting the
							// promise above, which would leave the caller awaiting forever, so a malformed
							// payload has to be turned into a rejection rather than dereferenced blindly.
							const task = response?.data;
							if (typeof task?.status !== 'string') {
								poll.cancel();
								reject(TRIAL_PRODUCT_FAILED_MESSAGE);
								return;
							}

							if (task.status.toLowerCase() === 'failed') {
								poll.cancel();
								reject(task.statusMessage || TRIAL_PRODUCT_FAILED_MESSAGE);
							}
							if (task.status.toLowerCase() === 'succeeded') {
								poll.cancel();
								resolve(task.statusMessage || TRIAL_PRODUCT_SUCCEEDED_MESSAGE);
							}
						},
						(error) => {
							poll.cancel();
							reject(error?.response?.data?.detail || TRIAL_PRODUCT_FAILED_MESSAGE);
						}
					);
					return jobId;
				})
				.catch((error) => {
					reject(error?.response?.data?.detail || TRIAL_PRODUCT_FAILED_MESSAGE);
				});
		});
	};
  
	// /licensing/products carries currentUpgradeChannel but accepts no companyId filter, so the
	// channel used to be read one product at a time — and fanning that out across every machine
	// was exhausting rest-api's connection pool. The /licensing/products/batch endpoint takes a
	// comma-separated productIds query and omits unknown IDs silently, so one call replaces the
	// fan-out. Products that never synced come back with currentUpgradeChannel === null, and
	// omitted IDs leave their key absent from the map (consumers treat absence as unknown).
	// The same call carries each machine's add-ons (productAddons). An older rest-api omits the
	// field, which is reported as null so "unknown" stays distinguishable from "no add-ons" ([]).
	getProductsBatch = (productIds) => {
		return this.restAxios.get('/licensing/products/batch', { params: { productIds: productIds.join(',') } })
			.then((response) => {
				const byProductId = {};
				response.data.forEach((product) => {
					byProductId[product.id] = {
						currentUpgradeChannel: product.currentUpgradeChannel ?? null,
						productAddons: Array.isArray(product.productAddons) ? product.productAddons : null,
					};
				});
				return byProductId;
			});
	};

	getTrialActivationKeys = (email) => {
		return this.restAxios.get(`/licensing/trial-activation-keys`, { params: { email, used: false } })
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				throw error?.response?.data?.detail ?? TRIAL_ACTIVATION_KEYS_FAILED_MESSAGE;
			});
	};
}
